import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { SelectInput, TextInput } from "../../components/ui/form";
import Workspace from "../../components/workspace";
import authService from "../../services/auth-service";
import { useBranches } from "../../state/branchListContext";
import { sortRecordsByObjectProp } from "../../utils/stripEmptyObjectProp";
import { serviceReportService } from "./service-report.service";
import { validateServiceReport } from "./service-report.validator";

const formatChildrenInputField = ({ values, toServer }) => {
   return toServer
      ? Object.assign({}, values, { children: values.childrenn })
      : Object.assign({}, values, { childrenn: values.children });
};

const ServiceReportForm = ({ modal, id, setSelected = () => {} }) => {
   const { branchCode } = authService.getUserInfo();
   const { branches, userCanSwitchBranch } = useBranches();
   const [serviceReport, setServiceReport] = useState({
      branchCode,
      serviceDate: new Date().toJSON().slice(0, 10),
   });

   const [isLoading, setIsLoading] = useState(id);
   const [successNotificationText, setSuccessNotificationText] = useState("");
   const [fetchError, setFetchError] = useState("");
   const [submitError, setSubmitError] = useState();

   const heading = id ? "Edit Service Report" : "Add New Service Report";
   const controller = new AbortController();

   useEffect(() => {
      async function loadServiceDetails() {
         try {
            const fetchResult = await serviceReportService.getServiceReport(id);
            setServiceReport(formatChildrenInputField({ values: fetchResult.data }));
         } catch (error) {
            setFetchError(error.message);
         } finally {
            setIsLoading(false);
         }
      }
      id && loadServiceDetails();

      return () => {
         setFetchError("");
         setIsLoading(id);
         setSubmitError("");
      };
   }, [id]);

   const handleSubmit = async (service) => {
      setSubmitError(null);

      service = formatChildrenInputField({ values: service, toServer: true });

      try {
         const submitResult = await serviceReportService.submitServiceReport({
            service,
            editOrCreate: id ? "edit" : "create",
         });
         setSuccessNotificationText(submitResult.message);
      } catch (error) {
         setSubmitError(error.message);
      }
   };
   const reset = () => {
      setSuccessNotificationText(null);
      setServiceReport({
         branchCode,
         serviceDate: new Date().toJSON().slice(0, 10),
      });
      setSubmitError("");
      setSelected({});
   };

   if (isLoading) return <Workspace.LoadingModal controller={controller} heading={heading} modal={modal} />;
   if (fetchError)
      return <Workspace.FetchErrorModal modal={modal} heading={heading} errorMessage={fetchError} callback={reset} />;
   if (successNotificationText)
      return <Workspace.SuccessNotificationModal modal={modal} message={successNotificationText} callback={reset} />;

   return (
      <Modal
         isOpen={modal.isOpen}
         onClose={() => {
            reset();
            modal.onClose();
         }}
         size="2xl">
         <ModalOverlay />
         <ModalContent className="!rounded-2xl">
            <Workspace.ModalHeader>
               <Workspace.ModalHeaderWithCloseButton>{heading}</Workspace.ModalHeaderWithCloseButton>
            </Workspace.ModalHeader>
            <ModalBody className="grid">
               <div className="py-5 px-4">
                  <div className="flex flex-wrap items-center">
                     <div className="w-full">
                        <Formik
                           initialValues={serviceReport}
                           validate={validateServiceReport}
                           onSubmit={handleSubmit}
                           enableReinitialize>
                           {({ isSubmitting }) => (
                              <Form className="grid gap-6">
                                 <SelectInput
                                    label="Branch"
                                    id="branchCode"
                                    name="branchCode"
                                    required
                                    disabled={!Boolean(userCanSwitchBranch())}>
                                    <option value=" ">Select branch</option>
                                    {branches &&
                                       sortRecordsByObjectProp(branches, "branchCode").map((_branch, key) => (
                                          <option value={_branch.branchCode} key={key}>
                                             {`${_branch.branchCode} - ${_branch.branchName}`}
                                          </option>
                                       ))}
                                 </SelectInput>
                                 <TextInput type="date" label="Date" id="serviceDate" name="serviceDate" required />
                                 <TextInput type="text" label="Service name" id="name" name="name" required />
                                 <TextInput type="text" label="Sermon title" id="sermonTitle" name="sermonTitle" required />
                                 <TextInput type="time" label="Start time" id="startTime" name="startTime" required />
                                 <TextInput type="time" label="Stop time" id="stopTime" name="stopTime" required />
                                 <TextInput type="number" label="Male" id="male" name="male" min={0} required />
                                 <TextInput type="number" label="Female" id="female" name="female" min={0} required />
                                 <TextInput type="number" label="Children" id="childrenn" name="childrenn" min={0} required />
                                 <TextInput type="number" label="Salvation" id="salvation" name="salvation" min={0} />
                                 <TextInput
                                    type="number"
                                    label="Holy Spirit Baptism"
                                    id="holySpiritBaptism"
                                    name="holySpiritBaptism"
                                    min={0}
                                 />
                                 <TextInput type="number" label="Testimonies" id="testimony" name="testimony" min={0} />
                                 <TextInput type="number" label="Prophesies" id="prophesy" name="prophesy" min={0} />
                                 <TextInput type="number" label="First Timer" id="firstTimer" name="firstTimer" min={0} />
                                 <TextInput type="number" label="Number of Vehicles" id="vehicle" name="vehicle" min={0} />

                                 <TextInput type="text" label="Note" id="note" name="note" placeholder="Additional note" />
                                 {submitError ? (
                                    <div className="mb-5">
                                       <Workspace.FormSubmitError>{submitError}</Workspace.FormSubmitError>
                                    </div>
                                 ) : null}
                                 <div className="mb-3 flex justify-end gap-2">
                                    <Button
                                       type="reset"
                                       variant="secondary"
                                       onClick={() => {
                                          controller.abort();
                                          modal.onClose();
                                       }}>
                                       Cancel
                                    </Button>
                                    <Button variant="primary" type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                       {!Boolean(id) ? "Create service record" : "Edit service record"}
                                    </Button>
                                 </div>
                              </Form>
                           )}
                        </Formik>
                     </div>
                  </div>
               </div>
            </ModalBody>
         </ModalContent>
      </Modal>
   );
};

export { ServiceReportForm };
